import { useState } from "react";
import { Loader, Lock } from "lucide-react";
import Modal from "@/app/components/Modal";
import { clientPaymentsApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { formatCurrencyFull } from "@/lib/data";
import { ClientPayment } from "./types";

interface EditPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  payment: ClientPayment | null;
  onUpdated: () => void;
}

const PAYMENT_METHODS = [
  { value: "cash", label: "Cash" },
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "cheque", label: "Cheque" },
  { value: "upi", label: "UPI" },
  { value: "card", label: "Card" },
];

/**
 * Admin-only modal to correct a recorded client payment amount.
 * Requires the admin payment-edit password (configured in General Settings).
 */
export default function EditPaymentModal({
  isOpen,
  onClose,
  payment,
  onUpdated,
}: EditPaymentModalProps) {
  const { showToast } = useToast();
  const [amount, setAmount] = useState<string>(payment ? String(payment.amount) : "");
  const [method, setMethod] = useState<string>(payment?.payment_method || "cash");
  const [adminPassword, setAdminPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!payment) return null;

  // Build the method options, preserving the payment's current method even if it
  // isn't one of the standard ones (payments created via "Take Payment" can use
  // arbitrary method strings).
  const methodOptions = [...PAYMENT_METHODS];
  const current = (payment.payment_method || "").trim();
  if (current && !methodOptions.some((m) => m.value === current)) {
    methodOptions.unshift({
      value: current,
      label: current.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
    });
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = parseFloat(amount);
    if (!parsed || parsed <= 0) {
      showToast("Enter a valid amount greater than 0.", "error");
      return;
    }
    if (!adminPassword.trim()) {
      showToast("Admin password is required to update a payment.", "error");
      return;
    }

    setSubmitting(true);
    try {
      await clientPaymentsApi.update(payment.id, {
        amount: parsed,
        payment_method: method,
        admin_password: adminPassword,
      });
      showToast("Payment updated successfully.", "success");
      setAdminPassword("");
      onUpdated();
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to update payment.";
      showToast(message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Payment Amount"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="rounded-lg bg-stone-50 border border-stone-200 px-3 py-2 text-xs text-stone-600">
          Current recorded amount:{" "}
          <span className="font-semibold text-stone-800">
            {formatCurrencyFull(payment.amount)}
          </span>
        </div>

        <div>
          <label htmlFor="edit-payment-amount" className="block text-sm font-medium text-stone-700 mb-1">
            New Amount
          </label>
          <input
            id="edit-payment-amount"
            type="number"
            step="0.01"
            min="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none"
            placeholder="Enter corrected amount"
            aria-label="New payment amount"
            required
          />
        </div>

        <div>
          <label htmlFor="edit-payment-method" className="block text-sm font-medium text-stone-700 mb-1">
            Payment Method
          </label>
          <select
            id="edit-payment-method"
            value={method}
            onChange={(e) => setMethod(e.target.value)}
            className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none"
            aria-label="Payment method"
          >
            {methodOptions.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="edit-payment-admin-password" className="block text-sm font-medium text-stone-700 mb-1 flex items-center gap-1">
            <Lock className="w-3.5 h-3.5 text-stone-400" />
            Admin Password
          </label>
          <input
            id="edit-payment-admin-password"
            type="password"
            value={adminPassword}
            onChange={(e) => setAdminPassword(e.target.value)}
            className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none"
            placeholder="Required to authorise this change"
            aria-label="Admin password"
            autoComplete="off"
            required
          />
          <p className="mt-1 text-[11px] text-stone-400">
            Editing a recorded payment requires the admin password set in General Settings.
          </p>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 rounded-lg text-sm font-medium text-stone-600 hover:bg-stone-100 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-amber-600 hover:bg-amber-700 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {submitting && <Loader className="w-4 h-4 animate-spin" />}
            Update Payment
          </button>
        </div>
      </form>
    </Modal>
  );
}
